<script setup lang="ts">
/**
 * El aviso de "no hay con qué llamarle a la IA", con las salidas pegadas.
 *
 * Vive fuera de las pantallas, debajo de la barra de arriba, porque el problema
 * tampoco es de una pantalla: mientras la credencial no sirva, ninguna tarea
 * agendada va a correr y todo lo demás que enseñe el tablero es paisaje.
 *
 * Aparece solo —se alimenta del pulso que la app ya sondea cada 12 segundos, así
 * que se entera aunque la credencial se haya caído a las tres de la mañana— y se
 * va solo en cuanto vuelve a servir. En medio ofrece las salidas que *este*
 * proveedor tenga: abrirte una terminal con el comando ya escrito, si es de los
 * que se firman; recibir el secreto pegado, si es de los que se pegan. Quién es
 * "este proveedor" lo contesta el servidor, así que esta pantalla no sabe de
 * Claude ni de nadie.
 */
import { computed, onBeforeUnmount, ref, watch } from "vue";
import type { SesionIaDto } from "@chuy/types";
import AppButton from "./AppButton.vue";
import AppIcon from "./AppIcon.vue";
import CredencialDialog from "./CredencialDialog.vue";
import { api } from "@/api/client";
import { useLimite } from "@/composables/useLimite";
import { useStatus } from "@/composables/useStatus";
import { useToast } from "@/composables/useToast";

/** Cada cuánto se pregunta mientras esperamos que alguien firme en la terminal. */
const SONDEO_MS = 15_000;
/** Cuánto se espera antes de rendirse y dejarlo en manos del botón. */
const ESPERA_MAX_MS = 5 * 60_000;

const { pulso, refresh } = useStatus();
const limite = useLimite();
const toast = useToast();

const sesion = ref<SesionIaDto | null>(null);
const revisando = ref(false);
const abriendo = ref(false);
const dialogoAbierto = ref(false);
const copiado = ref(false);
/** Prendido mientras se le abrió la terminal a alguien y no ha firmado. */
const esperandoFirma = ref(false);

let sondeo: ReturnType<typeof setInterval> | null = null;
let corte: ReturnType<typeof setTimeout> | null = null;

const vencida = computed(() => Boolean(pulso.value?.ia && !pulso.value.ia.ok));
const detalle = computed(() => sesion.value?.health.detail ?? pulso.value?.ia?.detail ?? "");
/**
 * El comando de volver a firmar, si este proveedor se arregla así.
 *
 * Sale de la receta del proveedor que manda, no de una constante: uno de llave
 * (ChatGPT, Gemini) no tiene comando, y entonces ni el chip ni el botón de la
 * terminal se pintan — ofrecer un botón que no puede cumplir es peor que no
 * tenerlo.
 */
const comando = computed(() => sesion.value?.comandoLogin ?? null);
/** Cómo se le dice al secreto de *este* proveedor. */
const etiqueta = computed(() => sesion.value?.etiqueta || "la credencial");

/**
 * Cuando el pulso avisa que se cayó, se piden los detalles una sola vez: quién
 * manda, cómo es su credencial, qué hay guardado y si esta máquina sabe abrir una
 * terminal. El pulso no los trae porque son de otra pregunta y no tiene caso
 * viajarlos cada 12 segundos.
 */
watch(vencida, async (malo) => {
  if (!malo) {
    dejarDeEsperar();
    return;
  }
  try {
    sesion.value = await api.system.ia();
  } catch {
    // Sin detalles la franja igual sirve: enseña el comando y el botón.
  }
});

function dejarDeEsperar(): void {
  esperandoFirma.value = false;
  if (sondeo) clearInterval(sondeo);
  if (corte) clearTimeout(corte);
  sondeo = null;
  corte = null;
}

onBeforeUnmount(dejarDeEsperar);

/**
 * Vuelve a preguntarle al proveedor, de verdad.
 *
 * `silencioso` es para los sondeos de fondo: mientras esperamos que alguien
 * firme en la terminal, un aviso cada 15 segundos diciendo "sigue vencida" sería
 * insoportable. El de la buena noticia sí sale siempre.
 */
async function revisar(silencioso = false): Promise<boolean> {
  if (!silencioso) revisando.value = true;
  try {
    const estado = await api.system.revisarIa();
    sesion.value = estado;
    await refresh();
    if (estado.health.ok) {
      dejarDeEsperar();
      toast.success("Listo, ya jalé. Le sigo con lo que quedó pendiente.");
      return true;
    }
    if (!silencioso) toast.error(estado.health.detail);
    return false;
  } catch (cause) {
    if (!silencioso) {
      toast.error(cause instanceof Error ? cause.message : "No pude revisar la sesión");
    }
    return false;
  } finally {
    revisando.value = false;
  }
}

/**
 * Abre la Terminal con el comando de firmar y se queda al pendiente.
 *
 * Chuy no puede ver lo que pasa en esa ventana, así que hace lo siguiente mejor:
 * preguntar cada 15 segundos si ya lo dejan pasar. En cuanto firmes, la franja se
 * va sola y las tareas pendientes salen sin que toques nada más.
 */
async function abrirTerminal(): Promise<void> {
  abriendo.value = true;
  try {
    await api.system.abrirTerminal("login");
    esperandoFirma.value = true;
    sondeo = setInterval(() => void revisar(true), SONDEO_MS);
    // Nadie firma en cinco minutos y sigue esperando: se corta el sondeo y queda
    // el botón, que para eso está.
    corte = setTimeout(dejarDeEsperar, ESPERA_MAX_MS);
  } catch (cause) {
    toast.error(cause instanceof Error ? cause.message : "No pude abrir la Terminal");
  } finally {
    abriendo.value = false;
  }
}

async function copiar(): Promise<void> {
  if (!comando.value) return;
  try {
    await navigator.clipboard.writeText(comando.value);
    copiado.value = true;
    setTimeout(() => (copiado.value = false), 1600);
  } catch {
    toast.error("No me dejaron copiar; cópialo a mano");
  }
}
</script>

<template>
  <!--
    Se acabó la cuota del proveedor.
    
    Va **antes** que el aviso de credencial y con su propio color: son las dos
    maneras de que Chuy no pueda trabajar, y confundirlas manda a la persona a
    hacer lo que no es. Aquí no hay botón de componer a propósito — no hay nada
    que componer. Lo único accionable es la hora, y ésa sí va en grande.
  -->
  <div
    v-if="limite.activo.value"
    class="animate-fade border-b border-separator bg-info-soft px-4 py-3 sm:px-8"
    role="status"
  >
    <div class="mx-auto flex w-full max-w-6xl flex-wrap items-start gap-x-3 gap-y-2">
      <span class="mt-0.5 shrink-0 text-accent"><AppIcon name="history" :size="16" /></span>
      <div class="min-w-0 flex-1">
        <p class="text-[13.5px] font-semibold text-content">{{ limite.mensaje.value }}</p>
        <p class="mt-0.5 text-[12.5px] leading-relaxed text-secondary">
          No es tu credencial ni un error: el servicio llegó a su límite de uso por ahorita y se repone solo.
          Las tareas agendadas guardan su turno y salen en cuanto vuelva.
        </p>
      </div>
      <span
        class="shrink-0 rounded-full bg-surface px-2.5 py-1 text-[12px] font-semibold tabular-nums text-secondary"
        :title="limite.horaDeVuelta.value ? `Vuelve a las ${limite.horaDeVuelta.value}` : ''"
      >
        {{ limite.horaDeVuelta.value || `faltan ${limite.faltan.value}` }}
      </span>
    </div>
  </div>

  <div
    v-if="vencida"
    class="animate-fade border-b border-separator bg-warn-soft px-4 py-3 sm:px-8"
    role="alert"
  >
    <div class="mx-auto flex w-full max-w-6xl flex-wrap items-start gap-x-3 gap-y-2.5">
      <span class="mt-0.5 shrink-0 text-warn"><AppIcon name="alert" :size="16" /></span>

      <div class="min-w-0 flex-1">
        <p class="text-[13.5px] font-semibold text-content">{{ detalle }}</p>
        <p class="mt-0.5 text-[12.5px] leading-relaxed text-secondary">
          <template v-if="esperandoFirma">
            Te abrí la Terminal. Firma ahí y yo me doy cuenta solo — no tienes que apretar nada
            más.
          </template>
          <template v-else-if="sesion && sesion.modo === 'llave' && sesion.puedeGuardar">
            Pega una llave nueva<template v-if="sesion.dondeSacarlo">
(se saca en
            <span class="font-mono">{{ sesion.dondeSacarlo.replace(/^https?:\/\//, "") }}</span>)
</template>.
            Mientras tanto, las tareas agendadas quedan en pausa —con su turno guardado— y en cuanto
            sirva otra vez, todo lo pendiente sale solo.
          </template>
          <template v-else-if="sesion && sesion.comandoToken && !sesion.puedeAbrirTerminal && sesion.puedeGuardar">
            <!--
              Chuy no corre en tu máquina, sino en un servidor: no hay terminal
              que abrir ahí ni sesión que firmar. Decirle a alguien «corre
              el comando de firmar» en este caso es mandarlo a una máquina a la
              que no se puede entrar. Lo que sí se puede hacer desde aquí es
              pegar un token, y eso es lo que se ofrece.
            -->
            Saca {{ sesion.etiqueta }} con <span class="font-mono">{{ sesion.comandoToken }}</span> en tu
            computadora y pégalo aquí abajo. Mientras tanto, las tareas agendadas quedan en pausa
            —con su turno guardado— y en cuanto sirva otra vez, todo lo pendiente sale solo.
          </template>
          <template v-else>
            Las tareas agendadas quedan en pausa —con su turno guardado— hasta que la arregles.
            En cuanto sirva otra vez, todo lo pendiente sale solo.
          </template>
        </p>

        <div class="mt-2 flex flex-wrap items-center gap-2">
          <!--
            El comando solo se enseña cuando de veras hay dónde correrlo: si
            Chuy vive en un servidor, el comando de firmar (el `comandoLogin` de
            la receta) no aplica —no hay sesión de esa máquina que firmar— y
            ofrecerlo para copiar manda a la gente a perder el rato. Ahí el camino es el token, que va abajo.
          -->
          <button
            v-if="comando && sesion?.puedeAbrirTerminal"
            class="inline-flex max-w-full items-center gap-1.5 rounded-[8px] border border-separator bg-surface px-2 py-1 text-left font-mono text-[12px] wrap-anywhere text-content transition-colors hover:bg-surface-hover"
            :title="copiado ? 'Copiado' : 'Copiar el comando'"
            @click="copiar"
          >
            <AppIcon :name="copiado ? 'check' : 'file'" :size="12" class="shrink-0 text-tertiary" />
            {{ comando }}
          </button>
          <span
            v-if="esperandoFirma"
            class="inline-flex items-center gap-1.5 text-[12px] text-tertiary"
          >
            <span
              class="animate-spin-slow size-3 rounded-full border-[1.6px] border-current border-t-transparent"
            />
            Esperando a que firmes…
          </span>
        </div>
      </div>

      <!-- Mismo caso que en Quién entra: `shrink-0` en una fila de botones que
           no cabe la saca de la tarjeta y el `overflow-hidden` se la come.
           Ancho completo en teléfono para que `flex-wrap` sí tenga sobre qué
           envolver; de `sm` para arriba, como siempre. -->
      <div class="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:shrink-0">
        <AppButton
          v-if="sesion?.puedeAbrirTerminal && !esperandoFirma"
          variant="primary"
          size="sm"
          icon="external"
          :loading="abriendo"
          @click="abrirTerminal"
        >
          Ábreme la terminal
        </AppButton>
        <AppButton
          v-if="sesion?.puedeGuardar"
          :variant="sesion.puedeAbrirTerminal ? 'secondary' : 'primary'"
          size="sm"
          icon="bolt"
          @click="dialogoAbierto = true"
        >
          Pegar {{ etiqueta }}
        </AppButton>
        <RouterLink v-if="sesion?.modo === 'ninguna'" to="/ajustes">
          <AppButton variant="primary" size="sm" icon="settings">Escoger un servicio de IA</AppButton>
        </RouterLink>
        <AppButton v-else size="sm" icon="refresh" :loading="revisando" @click="revisar(false)">
          Ya me reconecté
        </AppButton>
      </div>
    </div>

    <CredencialDialog
      :open="dialogoAbierto"
      :sesion="sesion"
      @close="dialogoAbierto = false"
      @changed="
        (estado: SesionIaDto) => {
          sesion = estado;
          void refresh();
        }
      "
    />
  </div>
</template>
